import { useState, useEffect, useRef } from 'react';
import {
  IonButton, IonInput, IonTextarea, IonSelect,
  IonSelectOption, IonLabel, IonIcon, IonToast, IonToggle, IonSpinner,
} from '@ionic/react';
import { camera, personOutline, briefcaseOutline, locationOutline, cashOutline, eyeOutline } from 'ionicons/icons';
import { z } from 'zod';

import { availabilityEnum, workTypeEnum, PROVINCES } from '@/lib/validation/registration';
import { DesiredLocationEditor } from '@/components/candidate/DesiredLocationEditor';
import { useMyCandidateProfile, useUpdateCandidateProfile, useUploadFile } from '@/lib/api/hooks';
import type { DesiredLocation } from '@/types';

const profileSchema = z.object({
  headline: z.string().min(5, 'Headline must be at least 5 characters'),
  bio: z.string().optional(),
  availability: z.enum(availabilityEnum),
  workType: z.enum(workTypeEnum),
  province: z.string().min(1, 'Select a province'),
  city: z.string().min(2, 'Enter a city or suburb'),
  salaryMin: z.number().min(0).optional(),
  salaryMax: z.number().min(0).optional(),
});

interface ProfileForm {
  headline: string;
  bio: string;
  availability: string;
  workType: string;
  province: string;
  city: string;
  salaryMin: string;
  salaryMax: string;
  isMarketplaceVisible: boolean;
  desiredLocations: DesiredLocation[];
}

interface ProfileEditPanelProps {
  onSaved?: () => void;
}

export function ProfileEditPanel({ onSaved }: ProfileEditPanelProps) {
  const { data: envelope, isLoading: loadingProfile } = useMyCandidateProfile();
  const profile = envelope?.data;
  const updateProfile = useUpdateCandidateProfile();
  const uploadFile = useUploadFile();

  const [form, setForm] = useState<ProfileForm>({
    headline: '',
    bio: '',
    availability: 'Contract',
    workType: 'Contract',
    province: '',
    city: '',
    salaryMin: '',
    salaryMax: '',
    isMarketplaceVisible: false,
    desiredLocations: [],
  });

  const [initialized, setInitialized] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<{ msg: string; color: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    if (profile && !initialized) {
      setForm({
        headline: profile.headline ?? '',
        bio: profile.bio ?? '',
        availability: profile.availability ?? 'Contract',
        workType: profile.workType ?? 'Contract',
        province: profile.plusCodeShort?.split(',').pop()?.trim() ?? '',
        city: profile.plusCodeShort?.split(',')[0]?.trim() ?? '',
        salaryMin: profile.desiredSalaryMin?.toString() ?? '',
        salaryMax: profile.desiredSalaryMax?.toString() ?? '',
        isMarketplaceVisible: profile.isMarketplaceVisible ?? false,
        desiredLocations: profile.desiredLocations ?? [],
      });
      setInitialized(true);
    }
  }, [profile, initialized]);

  const set = (patch: Partial<ProfileForm>) => setForm((f) => ({ ...f, ...patch }));

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setToast({ msg: 'Max file size is 5 MB', color: 'danger' });
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setToast({ msg: 'Accepts JPEG, PNG, or WebP', color: 'danger' });
      return;
    }
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    const r = profileSchema.safeParse({
      headline: form.headline,
      bio: form.bio || undefined,
      availability: form.availability,
      workType: form.workType,
      province: form.province,
      city: form.city,
      salaryMin: form.salaryMin ? Number(form.salaryMin) : undefined,
      salaryMax: form.salaryMax ? Number(form.salaryMax) : undefined,
    });
    if (!r.success) {
      const e: Record<string, string> = {};
      for (const issue of r.error.issues) e[String(issue.path[0])] = issue.message;
      setErrors(e);
      return;
    }
    setErrors({});

    if (!profile?._id) {
      setToast({ msg: 'No profile found. Create one first.', color: 'danger' });
      return;
    }

    let profileImageRef: { type: 'filereference'; id: string } | undefined = profile.profileImageRef ?? undefined;

    if (selectedFile) {
      try {
        const uploadRes = await uploadFile.mutateAsync({
          file: selectedFile,
          subsystem: 'Identity',
          category: 'PersonalDocument',
          ownerId: profile._id,
          ownerType: 'candidateprofile',
        });
        profileImageRef = uploadRes.data.fileRef;
      } catch (err: any) {
        setToast({ msg: err.message || 'Photo upload failed', color: 'danger' });
        return;
      }
    }

    updateProfile.mutate(
      {
        id: profile._id,
        data: {
          headline: form.headline,
          bio: form.bio || undefined,
          availability: form.availability as typeof profile.availability,
          workType: form.workType as typeof profile.workType,
          desiredSalaryMin: form.salaryMin ? Number(form.salaryMin) : undefined,
          desiredSalaryMax: form.salaryMax ? Number(form.salaryMax) : undefined,
          isMarketplaceVisible: form.isMarketplaceVisible,
          desiredLocations: form.desiredLocations,
          profileImageRef,
        },
      },
      {
        onSuccess: () => {
          setToast({ msg: 'Profile saved', color: 'success' });
          onSaved?.();
        },
        onError: (err: Error) => {
          setToast({ msg: err.message || 'Failed to save profile', color: 'danger' });
        },
      },
    );
  };

  if (loadingProfile) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <IonSpinner name="crescent" />
        <p style={{ color: 'var(--bp-text-muted)', marginTop: 16 }}>Loading profile…</p>
      </div>
    );
  }

  return (
    <div className="pm-page">
      {/* Photo */}
      <div className="pm-photo-section" onClick={() => fileInputRef.current?.click()}>
        <div className="pm-photo-ring">
          {photoPreview ? (
            <img src={photoPreview} alt="Profile" className="pm-photo-img" />
          ) : (
            <div className="pm-photo-placeholder">
              <IonIcon icon={camera} />
            </div>
          )}
          <div className="pm-photo-badge">
            <IonIcon icon={camera} />
          </div>
        </div>
        <span className="pm-photo-label">{photoPreview ? 'Change photo' : 'Add photo'}</span>
        <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={handlePhotoSelect} />
      </div>

      {/* About You */}
      <FormSection icon={personOutline} title="About You">
        <FormField label="Headline" error={errors.headline}>
          <IonInput
            placeholder="e.g. Qualified Electrician"
            value={form.headline}
            onIonInput={(e) => set({ headline: e.detail.value! })}
          />
        </FormField>
        <FormField label="Bio" error={undefined}>
          <IonTextarea
            placeholder="Tell clients about your trade and experience"
            value={form.bio}
            onIonInput={(e) => set({ bio: e.detail.value! })}
            rows={3}
          />
        </FormField>
      </FormSection>

      {/* Work Preferences */}
      <FormSection icon={briefcaseOutline} title="Work Preferences">
        <FormField label="Availability" error={undefined}>
          <IonSelect value={form.availability} onIonChange={(e) => set({ availability: e.detail.value })}>
            {availabilityEnum.map((a) => <IonSelectOption key={a} value={a}>{a}</IonSelectOption>)}
          </IonSelect>
        </FormField>
        <FormField label="Work type" error={undefined}>
          <IonSelect value={form.workType} onIonChange={(e) => set({ workType: e.detail.value })}>
            {workTypeEnum.map((w) => <IonSelectOption key={w} value={w}>{w}</IonSelectOption>)}
          </IonSelect>
        </FormField>
      </FormSection>

      {/* Location */}
      <FormSection icon={locationOutline} title="Location">
        <FormField label="Province" error={errors.province}>
          <IonSelect value={form.province} onIonChange={(e) => set({ province: e.detail.value })}>
            {PROVINCES.map((p) => <IonSelectOption key={p} value={p}>{p}</IonSelectOption>)}
          </IonSelect>
        </FormField>
        <FormField label="City / Suburb" error={errors.city}>
          <IonInput
            value={form.city}
            onIonInput={(e) => set({ city: e.detail.value! })}
          />
        </FormField>
        <div className="pm-field-block">
          <IonLabel className="pm-field-label">Desired locations</IonLabel>
          <DesiredLocationEditor
            value={form.desiredLocations}
            onChange={(next) => set({ desiredLocations: next })}
          />
        </div>
      </FormSection>

      {/* Salary */}
      <FormSection icon={cashOutline} title="Salary Expectation">
        <div className="pm-salary-row">
          <FormField label="Minimum (ZAR)" error={undefined}>
            <IonInput type="number" placeholder="e.g. 15000" value={form.salaryMin} onIonInput={(e) => set({ salaryMin: e.detail.value! })} />
          </FormField>
          <FormField label="Maximum (ZAR)" error={undefined}>
            <IonInput type="number" placeholder="e.g. 25000" value={form.salaryMax} onIonInput={(e) => set({ salaryMax: e.detail.value! })} />
          </FormField>
        </div>
      </FormSection>

      {/* Visibility */}
      <FormSection icon={eyeOutline} title="Visibility">
        <div className="pm-toggle-row">
          <div className="pm-toggle-info">
            <IonLabel className="pm-toggle-label">Visible in marketplace</IonLabel>
            <span className="pm-toggle-hint">Allow employers to find your profile in search results</span>
          </div>
          <IonToggle checked={form.isMarketplaceVisible} onIonChange={(e) => set({ isMarketplaceVisible: e.detail.checked })} />
        </div>
      </FormSection>

      {/* Save button */}
      <IonButton expand="block" className="pm-save-btn" onClick={handleSave} disabled={updateProfile.isPending}>
        {updateProfile.isPending ? 'Saving…' : 'Save profile'}
      </IonButton>

      <IonToast isOpen={!!toast} message={toast?.msg} color={toast?.color} duration={2000} onDidDismiss={() => setToast(null)} />
    </div>
  );
}

function FormSection({ icon, title, children }: { icon: string; title: string; children: React.ReactNode }) {
  return (
    <div className="pm-section">
      <div className="pm-section-header">
        <IonIcon icon={icon} className="pm-section-icon" />
        <h3 className="pm-section-title">{title}</h3>
      </div>
      <div className="pm-section-body">
        {children}
      </div>
    </div>
  );
}

function FormField({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div className="pm-field">
      <IonLabel className="pm-field-label">{label}</IonLabel>
      {children}
      {error && <span className="pm-field-error">{error}</span>}
    </div>
  );
}
